/**
 * StayGroup HMS — Centralized API Service
 * All backend calls go through here. Token is auto-injected.
 */

const BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? `${window.location.protocol}//${window.location.host}/api` : 'http://localhost:4000/api');

// ─── TOKEN MANAGEMENT ────────────────────────────────────────────────────────
export function getToken() {
  return sessionStorage.getItem('hms_token');
}

export function setToken(token) {
  sessionStorage.setItem('hms_token', token);
}

export function clearToken() {
  sessionStorage.removeItem('hms_token');
  sessionStorage.removeItem('hms_session');
}

// ─── BASE FETCH ────────────────────────────────────────────────────────────
async function request(method, path, body = null, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...options,
  };

  try {
    const res = await fetch(`${BASE_URL}${path}`, config);
    let data;
    try { data = await res.json(); } catch (e) { data = {}; }
    
    if (!res.ok) {
      if (res.status === 401 && (
        data?.error === 'Session expired or logged in elsewhere' ||
        data?.error === 'Invalid token' ||
        data?.error === 'Invalid or expired token'
      )) {
        window.dispatchEvent(new CustomEvent('session-expired', { detail: data?.error }));
        // Stop execution cleanly here without crashing generic components if possible
      }
      throw new Error(data?.error || `Request failed: ${res.status}`);
    }
    return data;
  } catch (err) {
    if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
      throw new Error('Cannot connect to server. Please ensure the API server is running.');
    }
    throw err;
  }
}

const get = (path) => request('GET', path);
const post = (path, body) => request('POST', path, body);
const put = (path, body) => request('PUT', path, body);
const del = (path) => request('DELETE', path);

// ─── AUTH ─────────────────────────────────────────────────────────────────
export const authApi = {
  login: (username, password) => post('/auth/login', { username, password }),
  logout: () => post('/auth/logout'),
  changePassword: (oldPassword, newPassword) => post('/auth/change-password', { oldPassword, newPassword }),
};

// ─── HOTELS ───────────────────────────────────────────────────────────────
export const hotelsApi = {
  list: () => get('/hotels'),
  create: (data) => post('/hotels', data),
  update: (id, data) => put(`/hotels/${id}`, data),
  delete: (id) => del(`/hotels/${id}`),
  emptyDrawer: (id, data) => post(`/hotels/${id}/empty-drawer`, data),
};

// ─── USERS ────────────────────────────────────────────────────────────────
export const usersApi = {
  list: () => get('/users'),
  create: (data) => post('/users', data),
  update: (id, data) => put(`/users/${id}`, data),
  delete: (id) => del(`/users/${id}`),
  changePassword: (data) => put('/users/me/password', data),
};

// ─── GUESTS ───────────────────────────────────────────────────────────────
export const guestsApi = {
  list: (q) => get(`/guests${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id) => get(`/guests/${id}`),
  create: (data) => post('/guests', data),
  update: (id, data) => put(`/guests/${id}`, data),
};

// ─── ROOM TYPES ───────────────────────────────────────────────────────────
export const roomTypesApi = {
  list: (hotelId) => get(`/room-types${hotelId ? `?hotelId=${hotelId}` : ''}`),
  create: (data) => post('/room-types', data),
  update: (id, data) => put(`/room-types/${id}`, data),
  delete: (id) => del(`/room-types/${id}`),
};

// ─── ROOMS ────────────────────────────────────────────────────────────────
export const roomsApi = {
  list: (hotelId) => get(`/rooms?hotelId=${hotelId}`),
  create: (data) => post('/rooms', data),
  update: (id, data) => put(`/rooms/${id}`, data),
  delete: (id) => del(`/rooms/${id}`),
};

// ─── RESERVATIONS ─────────────────────────────────────────────────────────
export const reservationsApi = {
  list: (hotelId, status, guestId, month, startDate, endDate) => {
    let q = `/reservations?_t=${Date.now()}`;
    if (hotelId) q += `&hotelId=${hotelId}`;
    if (status) q += `&status=${status}`;
    if (guestId) q += `&guestId=${guestId}`;
    if (month) q += `&month=${month}`;
    if (startDate) q += `&startDate=${startDate}`;
    if (endDate) q += `&endDate=${endDate}`;
    return get(q);
  },
  get: (id) => get(`/reservations/${id}`),
  checkAvailability: (params) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/reservations/availability?${qs}`);
  },
  checkAvailabilityCounts: (hotelId, checkIn, checkOut) => get(`/reservations/availability/counts?hotelId=${hotelId}&checkIn=${checkIn}&checkOut=${checkOut}`),
  create: (data) => post('/reservations', data),
  update: (id, data) => put(`/reservations/${id}`, data),
  delete: (id, hard = false) => del(`/reservations/${id}${hard ? '?hard=true' : ''}`),
  nightAuditToday: (hotelId) => get(`/reservations/night-audit/today?hotelId=${hotelId}`),
  markAudited: (id) => post(`/reservations/${id}/audit`),
  activity: (id) => get(`/reservations/${id}/activity`),
};

// ─── MAINTENANCE ──────────────────────────────────────────────────────────
export const maintenanceApi = {
  bulkDelete: (hotelId, month, startDate, endDate) => post('/maintenance/bulk-delete', { hotelId, month, startDate, endDate }),
};

// ─── PAYMENTS ─────────────────────────────────────────────────────────────
export const paymentsApi = {
  list: (hotelId) => get(`/payments?hotelId=${hotelId}`),
  forReservation: (reservationId) => get(`/payments?reservationId=${reservationId}`),
  create: (data) => post('/payments', data),
  update: (id, data) => put(`/payments/${id}`, data),
  delete: (id) => del(`/payments/${id}`),
  pendingOwnerCredits: (hotelId) => get(`/payments/pending-owner-credits?hotelId=${hotelId}`),
  settle: (id, data) => put(`/payments/${id}/settle`, data),
};

// ─── COMPANY DUES (GST company pays after checkout) ───────────────────────
export const companyDuesApi = {
  list: (hotelId, status = 'pending') => get(`/company-dues?hotelId=${hotelId}&status=${status}`),
  create: (data) => post('/company-dues', data),
  collect: (id, payments) => post(`/company-dues/${id}/payments`, { payments }),
};

// ─── CASH TRANSFERS (guest pays UPI, reception hands over cash) ────────────
export const cashTransfersApi = {
  create: (data) => post('/cash-transfers', data),
  delete: (transferId) => del(`/cash-transfers/${transferId}`),
};

// ─── EXPENSES ─────────────────────────────────────────────────────────────
export const expensesApi = {
  list: (hotelId) => get(`/expenses?hotelId=${hotelId}`),
  create: (data) => post('/expenses', data),
  update: (id, data) => put(`/expenses/${id}`, data),
  delete: (id) => del(`/expenses/${id}`),
};

// ─── OWNER PICKUPS ────────────────────────────────────────────────────────
export const ownerPickupsApi = {
  list: (hotelId, filters = {}) => {
    const qs = new URLSearchParams({ hotelId });
    if (filters.takenBy) qs.set('takenBy', filters.takenBy);
    if (filters.date) qs.set('date', filters.date);
    if (filters.start) qs.set('start', filters.start);
    if (filters.end) qs.set('end', filters.end);
    if (filters.recipientType) qs.set('recipientType', filters.recipientType);
    return get(`/owner-pickups?${qs.toString()}`);
  },
  create: (data) => post('/owner-pickups', data),
};

// ─── EXPENSE CATEGORIES ───────────────────────────────────────────────────
export const expenseCategoriesApi = {
  list: () => get('/expense-categories'),
  create: (data) => post('/expense-categories', data),
  delete: (id) => del(`/expense-categories/${id}`),
};

// ─── SHIFTS ───────────────────────────────────────────────────────────────
export const shiftsApi = {
  list: (hotelId) => get(`/shifts?hotelId=${hotelId}`),
  active: (hotelId) => get(`/shifts/active/${hotelId}`),
  create: (data) => post('/shifts', data),
  update: (id, data) => put(`/shifts/${id}`, data),
  getStats: (hotelId, startAt, userId) => {
    const qs = new URLSearchParams({ hotelId });
    if (startAt) qs.set('startAt', startAt);
    if (userId) qs.set('userId', userId);
    return get(`/shifts/stats-live?${qs.toString()}`);
  },
  historyStats: (hotelId, start, end) => get(`/shifts/stats/${hotelId}?start=${start}&end=${end}`),
  transactions: (hotelId, params = {}) => {
    const qs = new URLSearchParams();
    if (typeof params === 'string') {
      if (params) qs.set('sinceAt', params);
    } else if (params) {
      if (params.sinceAt) qs.set('sinceAt', params.sinceAt);
      if (params.date) qs.set('date', params.date);
    }
    return get(`/shifts/transactions/${hotelId}?${qs.toString()}`);
  },
};

export const tasksApi = {
  list: (hotelId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.room) qs.set('room', params.room);
    return get(`/tasks/${hotelId}?${qs.toString()}`);
  },
  create: (data) => post('/tasks', data),
  update: (id, data) => put(`/tasks/${id}`, data),
  updateStatus: (id, status) => put(`/tasks/${id}/status`, { status }),
  delete: (id) => del(`/tasks/${id}`),
};

// ─── INVOICES ─────────────────────────────────────────────────────────────
export const invoicesApi = {
  list: (hotelId) => get(`/invoices?hotelId=${hotelId}`),
  forReservation: (reservationId) => get(`/invoices?reservationId=${reservationId}`),
  create: (data) => post('/invoices', data),
  update: (id, data) => put(`/invoices/${id}`, data),
  nextNumber: (hotelId, billingType) => get(`/invoices/next-number?hotelId=${hotelId}&billingType=${billingType}`)
};

// ─── BILL SETTINGS ────────────────────────────────────────────────────────
export const billSettingsApi = {
  get: (hotelId) => get(`/bill-settings/${hotelId}`),
  save: (hotelId, data) => put(`/bill-settings/${hotelId}`, data),
};

// ─── ENQUIRIES ────────────────────────────────────────────────────────────
export const enquiriesApi = {
  list: (hotelId) => get(`/enquiries?hotelId=${hotelId}`),
  create: (data) => post('/enquiries', data),
  update: (id, data) => put(`/enquiries/${id}`, data),
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────
export const dashboardApi = {
  stats: (hotelId, start, end) => {
    let q = `/dashboard/${hotelId}`;
    let params = [];
    if (start) params.push(`start=${start}`);
    if (end) params.push(`end=${end}`);
    if (params.length) q += `?${params.join('&')}`;
    return get(q);
  },
};

// ─── AUDIT LOG ────────────────────────────────────────────────────────────
export const auditApi = {
  list: (hotelId) => get(`/audit-log?hotelId=${hotelId}`),
};

// ─── PROOFS ─────────────────────────────────────────────────────────────
export const proofsApi = {
  list: (guestId) => get(`/proofs?guestId=${guestId}`),
  create: (data) => post('/proofs', data),
  delete: (id) => del(`/proofs/${id}`),
};

// ─── DOCUMENTS OVERSIGHT (superadmin) ──────────────────────────────────────
const buildDocQuery = (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
  return new URLSearchParams(params).toString();
};
export const documentsApi = {
  listProofs: (filters) => get(`/documents/proofs?${buildDocQuery(filters)}`),
  getProof: (id) => get(`/documents/proofs/${id}`),
  listInvoices: (filters) => get(`/documents/invoices?${buildDocQuery(filters)}`),
  getInvoice: (id) => get(`/documents/invoices/${id}`),
  listExpenses: (filters) => get(`/documents/expenses?${buildDocQuery(filters)}`),
  getExpense: (id) => get(`/documents/expenses/${id}`),
};

// ─── LAUNDRY (internal linen tracking) ─────────────────────────────────────
export const laundryApi = {
  listBatches: (hotelId, status) => get(`/laundry/batches?hotelId=${hotelId}${status ? `&status=${status}` : ''}`),
  createBatch: (data) => post('/laundry/batches', data),
  receiveBatch: (id, data) => put(`/laundry/batches/${id}/receive`, data),
  closeBatch: (id) => put(`/laundry/batches/${id}/close`, {}),
};

// ─── INVENTORY ─────────────────────────────────────────────────────────────
export const inventoryApi = {
  categories: (hotelId) => get(`/inventory/categories?hotelId=${hotelId}`),
  createCategory: (data) => post('/inventory/categories', data),
  deleteCategory: (id) => del(`/inventory/categories/${id}`),
  items: (hotelId, categoryId) => get(`/inventory/items?hotelId=${hotelId}${categoryId ? `&categoryId=${categoryId}` : ''}`),
  createItem: (data) => post('/inventory/items', data),
  updateItem: (id, data) => put(`/inventory/items/${id}`, data),
  deleteItem: (id) => del(`/inventory/items/${id}`),
  logs: (hotelId, itemId) => get(`/inventory/logs?hotelId=${hotelId}${itemId ? `&itemId=${itemId}` : ''}`),
  createLog: (data) => post('/inventory/logs', data),
};

// ─── REPORTS ───────────────────────────────────────────────────────────────
export const reportsApi = {
  summary: (hotelId, start, end) => get(`/reports/summary?hotelId=${hotelId}${start ? `&start=${start}` : ''}${end ? `&end=${end}` : ''}`),
  gst: (hotelId, start, end) => get(`/reports/gst?hotelId=${hotelId}${start ? `&start=${start}` : ''}${end ? `&end=${end}` : ''}`),
  shifts: (hotelId, start, end) => get(`/reports/shifts?hotelId=${hotelId}${start ? `&start=${start}` : ''}${end ? `&end=${end}` : ''}`),
  occupancy: (hotelId, start, end) => get(`/reports/occupancy?hotelId=${hotelId}${start ? `&start=${start}` : ''}${end ? `&end=${end}` : ''}`),
  register: (hotelId, filters = {}) => {
    const params = { hotelId };
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.search) params.search = filters.search;
    return get(`/reports/register?${new URLSearchParams(params)}`);
  },
};

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────
export const notificationsApi = {
  list: (hotelId) => get(`/notifications?hotelId=${hotelId}`),
  markRead: (ids) => post('/notifications/read', { ids }),
};

// ─── HEALTH ───────────────────────────────────────────────────────────────
export const healthApi = {
  check: () => get('/health'),
};
